import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Typography
} from '@mui/material';
import {
  Download,
  History as HistoryIcon,
  Preview,
  Refresh,
  BarChart as BarChartIcon
} from '@mui/icons-material';
import { useToast } from '../../contexts/ToastContext';
import { formatHistoryUserName } from './reportHistoryUtils';
import ReportPreviewDialog from './ReportPreviewDialog';
import ReportVisualizeDialog from './ReportVisualizeDialog';
import {
  downloadAmpScheduleReportHistoryCsv,
  downloadAmpScheduleReportHistoryPdf,
  downloadAmpScheduleReportCsv,
  downloadAmpScheduleReportPdf,
  fetchAmpScheduleReportPreviewData,
  fetchAmpScheduleReportHistory,
  resolveAxiosErrorMessage
} from '../../utils/reportCenterApi';

const MAINTENANCE_TYPE_OPTIONS = [
  { value: 'ALL', label: 'All types' },
  { value: 'LINE_INSPECTION', label: 'Line Inspection' },
  { value: 'BASE_INSPECTION', label: 'Base Inspection' }
];

const INSPECTION_CATEGORY_OPTIONS = [
  { value: '', label: 'All categories' },
  { value: 'PHASE_INSPECTION', label: 'Phase Inspection' },
  { value: 'OUT_OF_PHASE_INSPECTION', label: 'Out of phase Inspection' },
  { value: 'LUBRICATION_INSPECTION', label: 'Lubrication Inspection' },
  { value: 'CONDITIONAL_INSPECTION', label: 'Conditional Inspection' },
  { value: 'ONE_TIME_INSPECTIONS', label: 'One Time Inspections' },
  { value: 'CORROSION_PREVENTION_AND_CONTROL', label: 'Corrosion Prevention and Control' }
];

const SCHEDULE_FILTER_OPTIONS = [
  { value: 'ALL', label: 'All' },
  { value: 'NOT_APPLICABLE', label: 'Not Applicable' },
  { value: 'EXCLUDE_NOT_APPLICABLE', label: 'Exclude Not Applicable' },
  { value: 'ONE_TIME', label: 'One time' },
  { value: 'EXCLUDE_ONE_TIME', label: 'Exclude one time' }
];

function todayDateValue() {
  const d = new Date();
  const offset = d.getTimezoneOffset();
  const local = new Date(d.getTime() - offset * 60 * 1000);
  return local.toISOString().slice(0, 10);
}

function formatDisplayDate(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

function formatDisplayDateTime(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
  });
}

function formatFileSize(bytes) {
  const n = Number(bytes) || 0;
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function formatFilterSummary(labels, fallback = 'All') {
  return labels?.length ? labels.join(', ') : fallback;
}

function formatAssemblyLabel(asm) {
  if (!asm) return '';
  const typeName = asm.typeName || 'Assembly';
  const sn = asm.serialNumber || 'N/A';
  const reg = asm.aircraftId?.registration;
  const base = `${typeName} – SN: ${sn}`;
  return reg ? `${base} (${reg})` : base;
}

/**
 * Dedicated AMP Schedule Report flow aligned with the AMP Schedules page,
 * including detail fields from the AMP create/edit dialog.
 */
const AmpScheduleReportPanel = ({ aircraftOptions = [], aircraftLabelById = {} }) => {
  const { showToast } = useToast();
  const [subTab, setSubTab] = useState(0);
  const [selectedAircraftIds, setSelectedAircraftIds] = useState([]);
  const [selectedAssemblyIds, setSelectedAssemblyIds] = useState([]);
  const [maintenanceType, setMaintenanceType] = useState('ALL');
  const [inspectionCategory, setInspectionCategory] = useState('');
  const [scheduleFilter, setScheduleFilter] = useState('ALL');
  const [search, setSearch] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [reportDate, setReportDate] = useState(todayDateValue);
  const [assemblyOptions, setAssemblyOptions] = useState([]);
  const [assemblyLoading, setAssemblyLoading] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [downloadingCsv, setDownloadingCsv] = useState(false);
  const [visualizing, setVisualizing] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewReportData, setPreviewReportData] = useState(null);
  const [visualizeOpen, setVisualizeOpen] = useState(false);
  const [visualizeData, setVisualizeData] = useState(null);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  const assemblyLabelById = useMemo(() => {
    const map = {};
    assemblyOptions.forEach((opt) => {
      map[opt.value] = opt.label;
    });
    return map;
  }, [assemblyOptions]);

  const loadAssemblies = useCallback(async (aircraftIds) => {
    setAssemblyLoading(true);
    try {
      const params = { isActive: 'true' };
      if (aircraftIds.length === 1) {
        params.aircraftId = aircraftIds[0];
      }
      const response = await axios.get('/api/assemblies', { params });
      const list = Array.isArray(response.data) ? response.data : (response.data?.data || []);
      list.sort((a, b) => {
        const la = `${a.typeName || ''} ${a.serialNumber || ''}`.toLowerCase();
        const lb = `${b.typeName || ''} ${b.serialNumber || ''}`.toLowerCase();
        return la.localeCompare(lb);
      });
      const options = list.map((asm) => ({
        value: asm._id,
        label: formatAssemblyLabel(asm)
      }));
      setAssemblyOptions(options);
      setSelectedAssemblyIds((prev) => prev.filter((id) => options.some((opt) => opt.value === id)));
    } catch (err) {
      console.error('Failed to load assemblies for AMP Schedule report:', err);
      setAssemblyOptions([]);
      setSelectedAssemblyIds([]);
    } finally {
      setAssemblyLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAssemblies(selectedAircraftIds);
  }, [selectedAircraftIds, loadAssemblies]);

  const loadHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const rows = await fetchAmpScheduleReportHistory();
      setHistory(rows);
    } catch (err) {
      showToast(await resolveAxiosErrorMessage(err) || 'Failed to load history', 'error');
    } finally {
      setHistoryLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    if (subTab === 1) {
      loadHistory();
    }
  }, [subTab, loadHistory]);

  const dateRangeError = useMemo(() => {
    if (dateFrom && dateTo && dateFrom > dateTo) {
      return 'Date From must be on or before Date To';
    }
    return '';
  }, [dateFrom, dateTo]);

  const fileStamp = dateFrom && dateTo ? `${dateFrom}_to_${dateTo}` : reportDate;

  const reportPayload = useMemo(() => ({
    aircraftIds: selectedAircraftIds,
    assemblyIds: selectedAssemblyIds,
    maintenanceType,
    inspectionCategory,
    scheduleFilter,
    search,
    dateFrom: dateFrom || undefined,
    dateTo: dateTo || undefined,
    reportDate
  }), [
    selectedAircraftIds,
    selectedAssemblyIds,
    maintenanceType,
    inspectionCategory,
    scheduleFilter,
    search,
    dateFrom,
    dateTo,
    reportDate
  ]);

  const handlePreview = async () => {
    if (dateRangeError) {
      showToast(dateRangeError, 'warning');
      return;
    }
    setPreviewing(true);
    try {
      const reportData = await fetchAmpScheduleReportPreviewData(reportPayload);
      setPreviewReportData(reportData);
      setPreviewOpen(true);
    } catch (err) {
      showToast(await resolveAxiosErrorMessage(err) || 'Preview failed', 'error');
    } finally {
      setPreviewing(false);
    }
  };

  const handleVisualize = async () => {
    if (dateRangeError) {
      showToast(dateRangeError, 'warning');
      return;
    }
    setVisualizing(true);
    try {
      const reportData = await fetchAmpScheduleReportPreviewData(reportPayload);
      setVisualizeData(reportData);
      setVisualizeOpen(true);
    } catch (err) {
      showToast(await resolveAxiosErrorMessage(err) || 'Visualization failed', 'error');
    } finally {
      setVisualizing(false);
    }
  };

  const handleDownload = async (visualizations) => {
    if (dateRangeError) {
      showToast(dateRangeError, 'warning');
      return;
    }
    setDownloading(true);
    try {
      await downloadAmpScheduleReportPdf(
        { ...reportPayload, visualizations },
        `AMP_Schedule_Report_${fileStamp}.pdf`
      );
      showToast(
        visualizations?.length
          ? 'AMP Schedule Report with visualizations downloaded and saved to history'
          : 'AMP Schedule Report downloaded and saved to history',
        'success'
      );
      if (subTab === 1) {
        loadHistory();
      }
    } catch (err) {
      showToast(await resolveAxiosErrorMessage(err) || 'Download failed', 'error');
    } finally {
      setDownloading(false);
    }
  };

  const handleDownloadCsv = async () => {
    if (dateRangeError) {
      showToast(dateRangeError, 'warning');
      return;
    }
    setDownloadingCsv(true);
    try {
      await downloadAmpScheduleReportCsv(reportPayload, `AMP_Schedule_Report_${fileStamp}.pdf`);
      showToast('AMP Schedule Report CSV downloaded', 'success');
    } catch (err) {
      showToast(await resolveAxiosErrorMessage(err) || 'CSV download failed', 'error');
    } finally {
      setDownloadingCsv(false);
    }
  };

  const handleHistoryDownload = (row) => {
    try {
      downloadAmpScheduleReportHistoryPdf(row._id, row.filename || 'AMP_Schedule_Report.pdf');
      showToast('Re-downloading stored PDF', 'success');
    } catch (err) {
      showToast(err.message || 'Re-download failed', 'error');
    }
  };

  const handleHistoryDownloadCsv = (row) => {
    try {
      downloadAmpScheduleReportHistoryCsv(row._id, row.filename || 'AMP_Schedule_Report.pdf');
      showToast('Downloading CSV', 'success');
    } catch (err) {
      showToast(err.message || 'CSV download failed', 'error');
    }
  };

  const busy = previewing || downloading || downloadingCsv || visualizing;

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        AMP Schedule Report
      </Typography>
      <Typography color="text.secondary" paragraph>
        Aircraft maintenance programme schedules with list columns plus detail fields from the AMP
        schedule dialog — description, assemblies, linked AD/SB, recurrence configuration, last
        performed, AMP checks, and required items. Filter by aircraft, assembly, maintenance type,
        inspection category, and schedule mode. Each PDF download is stored for later re-download.
      </Typography>

      <Tabs
        value={subTab}
        onChange={(_, v) => setSubTab(v)}
        sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}
      >
        <Tab label="Generate" />
        <Tab icon={<HistoryIcon fontSize="small" />} iconPosition="start" label="History" />
      </Tabs>

      {subTab === 0 && (
        <Paper variant="outlined" sx={{ p: 3 }}>
          <Stack spacing={2.5} maxWidth={640}>
            <TextField
              label="Search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              fullWidth
              placeholder="AMP #, title, or description"
              helperText="Matches AMP schedule number, title, and description"
            />

            <FormControl fullWidth>
              <InputLabel id="amp-schedule-report-aircrafts-label">Aircrafts</InputLabel>
              <Select
                labelId="amp-schedule-report-aircrafts-label"
                multiple
                value={selectedAircraftIds}
                label="Aircrafts"
                onChange={(e) => {
                  const value = e.target.value;
                  const next = typeof value === 'string' ? value.split(',') : value;
                  setSelectedAircraftIds(next);
                  setSelectedAssemblyIds([]);
                }}
                renderValue={(selected) => {
                  if (!selected.length) return 'All aircraft';
                  return selected.map((id) => aircraftLabelById[id] || id).join(', ');
                }}
              >
                {aircraftOptions.map((ac) => (
                  <MenuItem key={ac.value} value={ac.value}>
                    {ac.label}
                  </MenuItem>
                ))}
              </Select>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, ml: 1.75 }}>
                Optional — leave empty for all aircraft
              </Typography>
            </FormControl>

            <FormControl fullWidth disabled={assemblyLoading}>
              <InputLabel id="amp-schedule-report-assemblies-label">Assemblies</InputLabel>
              <Select
                labelId="amp-schedule-report-assemblies-label"
                multiple
                value={selectedAssemblyIds}
                label="Assemblies"
                onChange={(e) => {
                  const value = e.target.value;
                  setSelectedAssemblyIds(typeof value === 'string' ? value.split(',') : value);
                }}
                renderValue={(selected) => {
                  if (!selected.length) return 'All assemblies';
                  return selected.map((id) => assemblyLabelById[id] || id).join(', ');
                }}
              >
                {assemblyOptions.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, ml: 1.75 }}>
                {assemblyLoading
                  ? 'Loading assemblies…'
                  : selectedAircraftIds.length === 1
                    ? 'Filtered to assemblies on the selected aircraft'
                    : 'Optional — scoped to a single aircraft when one is selected'}
              </Typography>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel id="amp-schedule-report-maintenance-type-label">Maintenance Type</InputLabel>
              <Select
                labelId="amp-schedule-report-maintenance-type-label"
                value={maintenanceType}
                label="Maintenance Type"
                onChange={(e) => setMaintenanceType(e.target.value)}
              >
                {MAINTENANCE_TYPE_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel id="amp-schedule-report-inspection-category-label">Inspection Category</InputLabel>
              <Select
                labelId="amp-schedule-report-inspection-category-label"
                value={inspectionCategory}
                label="Inspection Category"
                onChange={(e) => setInspectionCategory(e.target.value)}
              >
                {INSPECTION_CATEGORY_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value || 'ALL'} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel id="amp-schedule-report-schedule-filter-label">Schedule Filter</InputLabel>
              <Select
                labelId="amp-schedule-report-schedule-filter-label"
                value={scheduleFilter}
                label="Schedule Filter"
                onChange={(e) => setScheduleFilter(e.target.value)}
              >
                {SCHEDULE_FILTER_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                label="Date from"
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                fullWidth
                InputLabelProps={{ shrink: true }}
                helperText="Optional — leave blank for all records"
              />
              <TextField
                label="Date to"
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                fullWidth
                InputLabelProps={{ shrink: true }}
                helperText="Optional — leave blank for all records"
              />
            </Stack>

            {dateRangeError && (
              <Alert severity="warning">{dateRangeError}</Alert>
            )}

            <TextField
              label="Report Date"
              type="date"
              value={reportDate}
              onChange={(e) => setReportDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />

            <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
              <Button
                variant="outlined"
                startIcon={visualizing ? <CircularProgress size={16} /> : <BarChartIcon />}
                onClick={handleVisualize}
                disabled={busy || Boolean(dateRangeError)}
              >
                Visualize
              </Button>
              <Button
                variant="outlined"
                startIcon={previewing ? <CircularProgress size={16} /> : <Preview />}
                onClick={handlePreview}
                disabled={busy || Boolean(dateRangeError)}
              >
                Preview
              </Button>
              <Button
                variant="contained"
                startIcon={downloading ? <CircularProgress size={16} color="inherit" /> : <Download />}
                onClick={() => handleDownload()}
                disabled={busy || Boolean(dateRangeError)}
              >
                Download PDF
              </Button>
              <Button
                variant="outlined"
                startIcon={downloadingCsv ? <CircularProgress size={16} /> : <Download />}
                onClick={handleDownloadCsv}
                disabled={busy || Boolean(dateRangeError)}
              >
                Download CSV
              </Button>
            </Stack>
          </Stack>
        </Paper>
      )}

      {subTab === 1 && (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
            <Typography variant="subtitle1">Downloaded AMP Schedule Reports</Typography>
            <Button
              size="small"
              startIcon={historyLoading ? <CircularProgress size={14} /> : <Refresh />}
              onClick={loadHistory}
              disabled={historyLoading}
            >
              Refresh
            </Button>
          </Box>

          {historyLoading && history.length === 0 ? (
            <Box py={4} textAlign="center">
              <CircularProgress size={28} />
            </Box>
          ) : history.length === 0 ? (
            <Alert severity="info">
              No AMP Schedule Report downloads yet. Generate one from the Generate tab.
            </Alert>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Downloaded</TableCell>
                  <TableCell>Generated by</TableCell>
                  <TableCell>Period</TableCell>
                  <TableCell>Search</TableCell>
                  <TableCell>Aircrafts</TableCell>
                  <TableCell>Assemblies</TableCell>
                  <TableCell>Maintenance Type</TableCell>
                  <TableCell>Inspection Category</TableCell>
                  <TableCell>Schedule Filter</TableCell>
                  <TableCell align="right">Schedules</TableCell>
                  <TableCell align="right">Size</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {history.map((row) => (
                  <TableRow key={row._id} hover>
                    <TableCell>{formatDisplayDateTime(row.downloadedAt)}</TableCell>
                    <TableCell>{formatHistoryUserName(row.downloadedBy)}</TableCell>
                    <TableCell>
                      {row.dateFrom || row.dateTo
                        ? `${formatDisplayDate(row.dateFrom)} – ${formatDisplayDate(row.dateTo)}`
                        : '—'}
                    </TableCell>
                    <TableCell>{row.search || '—'}</TableCell>
                    <TableCell>
                      {(row.aircraftLabels || []).length
                        ? row.aircraftLabels.join(', ')
                        : 'All aircraft'}
                    </TableCell>
                    <TableCell>
                      {formatFilterSummary(row.assemblyLabels)}
                    </TableCell>
                    <TableCell>
                      {row.maintenanceTypeLabel || 'All types'}
                    </TableCell>
                    <TableCell>
                      {row.inspectionCategoryLabel || 'All'}
                    </TableCell>
                    <TableCell>
                      {row.scheduleFilterLabel || 'All'}
                    </TableCell>
                    <TableCell align="right">{row.rowCount ?? '—'}</TableCell>
                    <TableCell align="right">{formatFileSize(row.fileSize)}</TableCell>
                    <TableCell align="right">
                      <Stack direction="row" spacing={1} justifyContent="flex-end">
                        <Button
                          size="small"
                          startIcon={<Download />}
                          onClick={() => handleHistoryDownload(row)}
                        >
                          PDF
                        </Button>
                        <Button
                          size="small"
                          startIcon={<Download />}
                          onClick={() => handleHistoryDownloadCsv(row)}
                        >
                          CSV
                        </Button>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Paper>
      )}

      <ReportPreviewDialog
        open={previewOpen}
        title="AMP Schedule Report preview"
        reportData={previewReportData}
        onClose={() => {
          setPreviewOpen(false);
          setPreviewReportData(null);
        }}
        onDownload={() => handleDownload()}
        downloading={downloading}
        onDownloadCsv={handleDownloadCsv}
        downloadingCsv={downloadingCsv}
      />

      <ReportVisualizeDialog
        open={visualizeOpen}
        title="AMP Schedule Report"
        data={visualizeData}
        downloading={downloading}
        onClose={() => {
          setVisualizeOpen(false);
          setVisualizeData(null);
        }}
        onDownloadPdfWithVisualization={(visualizations) => handleDownload(visualizations)}
      />
    </Box>
  );
};

export default AmpScheduleReportPanel;
